import React, { useEffect, useMemo, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import {
  Alert,
  Box,
  Button,
  Checkbox,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControl,
  FormLabel,
  List,
  ListItem,
  Modal,
  ModalDialog,
  Option,
  Select,
  Sheet,
  Skeleton,
  Stack,
  Typography,
} from "@mui/joy";
import {
  formatMoney,
  formatQuoteRange,
  mailBatchesApi,
  mailCampaignsApi,
  mailerPinsApi,
} from "../../lib/directMailApi";
import { getApiErrorMessage } from "../../lib/apiError";
import {
  MAIL_TEMPLATE_LABELS,
  type MailBatchCreateResponse,
  type MailCampaign,
} from "../../types/directMail";
import { loadAllPages } from "./loadAllPages";
import { mailWorkflows, mailPageLabel } from "./mailWorkflows";

export interface CreateBatchModalProps {
  open: boolean;
  onClose: () => void;
  onCreated: (batch: MailBatchCreateResponse) => void;
}

export const CreateBatchModal: React.FC<CreateBatchModalProps> = ({
  open,
  onClose,
  onCreated,
}) => {
  const [campaignId, setCampaignId] = useState<number | null>(null);
  const [selectedIds, setSelectedIds] = useState<number[]>([]);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const campaignsQuery = useQuery({
    queryKey: ["direct-mail", "campaigns", "picker"],
    queryFn: () => loadAllPages((page) => mailCampaignsApi.list({ page })),
    enabled: open,
    staleTime: 5 * 60 * 1000,
  });

  const approvedQuery = useQuery({
    queryKey: ["direct-mail", "pins", "approved"],
    queryFn: () =>
      loadAllPages((page) => mailerPinsApi.list({ status: "approved", page })),
    enabled: open,
  });

  useEffect(() => {
    if (!open) return;
    setSelectedIds([]);
    setError(null);
  }, [open]);

  const campaigns = (campaignsQuery.data ?? []).filter(
    (campaign: MailCampaign) => campaign.status !== "archived",
  );

  useEffect(() => {
    if (campaignId === null && campaigns.length > 0)
      setCampaignId(campaigns[0].id);
  }, [campaignId, campaigns]);

  const campaign = campaigns.find((entry) => entry.id === campaignId) ?? null;

  // A pin already tied to a different campaign is rejected by the API, so it is
  // never offered here — only this campaign's pins and unassigned ones.
  const eligible = useMemo(
    () =>
      (approvedQuery.data ?? []).filter(
        (pin) => pin.campaign === null || pin.campaign === campaignId,
      ),
    [approvedQuery.data, campaignId],
  );

  useEffect(() => {
    setSelectedIds((current) =>
      current.filter((id) => eligible.some((pin) => pin.id === id)),
    );
  }, [eligible]);

  const plan = useQuery({
    queryKey: ["direct-mail", "cost-plan", campaignId, selectedIds],
    queryFn: () => mailWorkflows.plan(campaignId!, selectedIds),
    enabled: open && !!campaignId && selectedIds.length > 0,
  });
  const unitPrice = plan.data?.unit_price ?? null;
  const total = plan.data?.total_amount ?? null;

  const allSelected =
    eligible.length > 0 && selectedIds.length === eligible.length;

  const create = async () => {
    if (!campaign || selectedIds.length === 0) return;
    setSaving(true);
    setError(null);
    try {
      const batch = await mailBatchesApi.create({
        campaign: campaign.id,
        pin_ids: selectedIds,
      });
      onCreated(batch);
    } catch (err) {
      setError(getApiErrorMessage(err, "That batch could not be created."));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal open={open} onClose={onClose}>
      <ModalDialog
        layout="center"
        sx={{
          width: { xs: "94vw", md: 640 },
          maxHeight: "92vh",
          overflow: "hidden",
        }}
      >
        <DialogTitle>Create a mailing batch</DialogTitle>
        <DialogContent sx={{ overflowY: "auto" }}>
          <Stack spacing={2} sx={{ pt: 1 }}>
            <Typography
              level="body-sm"
              sx={{ color: "text.tertiary", display: "block" }}
            >
              A batch starts as a draft. Nothing is printed and nothing is
              charged until you render the proof, read it, and submit.
            </Typography>

            {error && (
              <Alert color="danger" variant="soft">
                {error}
              </Alert>
            )}

            <FormControl>
              <FormLabel>Campaign</FormLabel>
              <Select
                value={campaignId}
                onChange={(_, value) => setCampaignId(value)}
                placeholder={
                  campaigns.length === 0
                    ? "No campaigns yet"
                    : "Choose a campaign"
                }
                disabled={campaigns.length === 0}
                slotProps={{ button: { "aria-label": "Campaign" } }}
              >
                {campaigns.map((entry) => (
                  <Option key={entry.id} value={entry.id}>
                    {entry.name} — {MAIL_TEMPLATE_LABELS[entry.template_kind]}
                  </Option>
                ))}
              </Select>
            </FormControl>

            <Divider />

            <Box>
              <Stack
                direction="row"
                justifyContent="space-between"
                alignItems="center"
              >
                <Typography level="title-sm">Approved pins</Typography>
                {eligible.length > 0 && (
                  <Button
                    size="sm"
                    variant="plain"
                    color="neutral"
                    onClick={() =>
                      setSelectedIds(
                        allSelected ? [] : eligible.map((pin) => pin.id),
                      )
                    }
                  >
                    {allSelected ? "Clear" : `Select all ${eligible.length}`}
                  </Button>
                )}
              </Stack>

              {approvedQuery.isLoading ? (
                <Stack spacing={1} sx={{ mt: 1 }}>
                  {[0, 1, 2].map((row) => (
                    <Skeleton
                      key={row}
                      variant="rectangular"
                      height={40}
                      sx={{ borderRadius: "sm" }}
                    />
                  ))}
                </Stack>
              ) : eligible.length === 0 ? (
                <Sheet
                  variant="soft"
                  sx={{ p: 3, borderRadius: "sm", textAlign: "center", mt: 1 }}
                >
                  <Typography level="title-sm">
                    Nothing is approved to mail yet
                  </Typography>
                  <Typography
                    level="body-sm"
                    sx={{ color: "text.tertiary", mt: 0.5 }}
                  >
                    Measure pins in the queue and approve their prices. Approved
                    pins for this campaign — and any pin not yet tied to a
                    campaign — show up here.
                  </Typography>
                </Sheet>
              ) : (
                <Sheet
                  variant="outlined"
                  sx={{
                    borderRadius: "sm",
                    maxHeight: 280,
                    overflowY: "auto",
                    mt: 1,
                  }}
                >
                  <List size="sm">
                    {eligible.map((pin) => (
                      <ListItem key={pin.id}>
                        <Checkbox
                          size="sm"
                          checked={selectedIds.includes(pin.id)}
                          onChange={() =>
                            setSelectedIds((current) =>
                              current.includes(pin.id)
                                ? current.filter((id) => id !== pin.id)
                                : [...current, pin.id],
                            )
                          }
                          label={
                            <Box>
                              <Typography
                                level="body-sm"
                                sx={{ display: "block" }}
                              >
                                {pin.formatted_address}
                              </Typography>
                              <Typography
                                level="body-xs"
                                sx={{
                                  color: "text.tertiary",
                                  display: "block",
                                }}
                              >
                                {formatQuoteRange(
                                  pin.quote_price_low,
                                  pin.quote_price_high,
                                )}
                                {pin.campaign === null
                                  ? " · no campaign yet"
                                  : ""}
                              </Typography>
                            </Box>
                          }
                        />
                      </ListItem>
                    ))}
                  </List>
                </Sheet>
              )}
            </Box>

            {plan.isError && (
              <Alert color="danger">
                {getApiErrorMessage(
                  plan.error,
                  "Cost plan could not be loaded.",
                )}
                <Button onClick={() => void plan.refetch()}>Retry</Button>
              </Alert>
            )}
            {plan.data && (
              <>
                <Typography level="body-sm">
                  Pages:{" "}
                  {plan.data.included_pages.map(mailPageLabel).join(", ") ||
                    "Postcard front and back"}
                </Typography>
                {plan.data.excluded.length > 0 && (
                  <Alert color="warning">
                    <Stack>
                      <Typography>
                        {plan.data.excluded.length} houses excluded
                      </Typography>
                      {plan.data.excluded.map((row) => (
                        <Typography key={row.id} level="body-sm">
                          {row.address || `Pin ${row.id}`}: {row.reason}
                        </Typography>
                      ))}
                    </Stack>
                  </Alert>
                )}
                <Typography level="body-xs">{plan.data.note}</Typography>
              </>
            )}
            <Sheet variant="soft" sx={{ p: 1.5, borderRadius: "sm" }}>
              <Stack direction="row" justifyContent="space-between">
                <Typography level="body-sm">Pieces</Typography>
                <Typography level="body-sm">
                  {plan.data?.piece_count ?? "—"} included /{" "}
                  {selectedIds.length} selected
                </Typography>
              </Stack>
              <Stack direction="row" justifyContent="space-between">
                <Typography level="body-sm">
                  Price per piece
                  {campaign
                    ? ` (${MAIL_TEMPLATE_LABELS[campaign.template_kind]})`
                    : ""}
                </Typography>
                <Typography level="body-sm">
                  {unitPrice === null ? "—" : formatMoney(unitPrice)}
                </Typography>
              </Stack>
              <Divider sx={{ my: 1 }} />
              <Stack direction="row" justifyContent="space-between">
                <Typography level="title-sm">Total when you submit</Typography>
                <Typography level="title-sm">
                  {total === null ? "—" : formatMoney(total)}
                </Typography>
              </Stack>
            </Sheet>
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button
            loading={saving}
            disabled={
              !campaign ||
              !plan.data?.piece_count ||
              plan.isFetching ||
              plan.isError
            }
            onClick={() => void create()}
          >
            Create draft batch
          </Button>
          <Button variant="plain" color="neutral" onClick={onClose}>
            Cancel
          </Button>
        </DialogActions>
      </ModalDialog>
    </Modal>
  );
};
